import {
  BackButtonCWUI,
  ButtonUI,
  CloseButtonCWUI,
} from '@next-order/next-order-components';
import React, {
  useEffect,
  useReducer,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  assignPointsToUserScan,
  clearPendingRedemptionIntent,
  createLoyaltyUserSignup,
  fetchLoyaltyDetailsSuccess,
  handleRedemption,
  showEnrollmentForm,
  updateLoyaltyAccount,
  searchAddresses,
  getAddressByPlaceId,
} from './actions';
import { selectStoreConfig } from 'containers/Layout/selectors';
import { toggleMobileCart } from 'containers/Home/actions';
import { useAuth } from 'contexts/AuthProvider';
import parsePhoneNumberFromString, { AsYouType } from 'libphonenumber-js';
import { validateEmail, validatePostCode } from 'helpers/validationUtils';
import { ORDER_STAGE_TYPES } from 'helpers/enums';
import { useLoyaltyRoute } from 'helpers/hooks/useLoyaltyRoute';
import debounce from 'lodash/debounce';
import { ReactComponent as Close } from 'assets/images/icon-close.svg';
import LottieAnimation from '../Lottie';
import AnimationWrapper from '../AnimationWrapper';
import { LOYALTY_DEFAULTS } from './constants';
import { isLightColor } from './utils';

// Reducer function
function formReducer(state, action) {
  switch (action.type) {
    case 'UPDATE_FIELD':
      return {
        ...state,
        values: {
          ...state.values,
          [action.field]: action.value,
        },
        errors: {
          ...state.errors,
          [action.field]: '', // clear error on change
        },
      };
    case 'SET_ERRORS':
      return {
        ...state,
        errors: action.errors,
      };
    case 'RESET':
      return action.initialState;
    default:
      return state;
  }
}

export default function EnrollmentForm({ enrollmentFields }) {
  const { userLoyaltyDetails, storeLoyaltySettings, pendingRedemptionIntent } =
    useSelector(
    (state) => state.loyaltyReducer
    );
  const reduxDispatch = useDispatch();
  const { userDetails } = useSelector((state) => state.homeReducer);
  const storeConfig = useSelector(selectStoreConfig);
  const { storeId, restaurantId } = storeConfig;
  const storeSettings = storeLoyaltySettings || null;
  const themeColors = storeSettings?.themeSettings
    ? {
        backgroundColor:
          storeSettings.themeSettings.selectedBackgroundColor || '#f9f9f9',
        labelTextColor:
          storeSettings.themeSettings.selectedLabelTextColor || '#333',
        mainTextColor:
          storeSettings.themeSettings.selectedMainTextColor || '#666',
      }
    : {
        backgroundColor: '#f9f9f9',
        labelTextColor: '#333',
        mainTextColor: '#666',
      };
  const isLightBackground = isLightColor(themeColors.backgroundColor);
  const normalizedBackgroundColor = themeColors.backgroundColor.toLowerCase();
  const normalizedMainTextColor = themeColors.mainTextColor.toLowerCase();
  const fallbackButtonTextColor = isLightBackground ? '#111111' : '#ffffff';
  const buttonTextColor =
    normalizedBackgroundColor === normalizedMainTextColor
      ? fallbackButtonTextColor
      : themeColors.mainTextColor;
  const signupButtonStyle = {
    backgroundColor: themeColors.backgroundColor,
    color: buttonTextColor,
    ...(isLightBackground && {
      border: '1px solid rgba(0, 0, 0, 0.2)',
    }),
  };
  const loadingStatusText = useMemo(() => {
    if (!userLoyaltyDetails) {
      return 'Signing up to loyalty...';
    }
    if (pendingRedemptionIntent?.redemptionId) {
      return 'Completing profile and redeeming reward...';
    }
    return 'Completing your profile...';
  }, [userLoyaltyDetails, pendingRedemptionIntent]);
  const { setState: setAuthState } = useAuth();
  const { isLoyalty, orderId, referralId } = useLoyaltyRoute();
  const injectedEmailFieldId = '__requiredEmail__';
  const hasProfileEmail = Boolean(userDetails?.email);
  const effectiveEnrollmentFields = useMemo(() => {
    const fields = Array.isArray(enrollmentFields) ? enrollmentFields : [];
    const hasEmailField = fields.some((field) => field.type === 'email');
    if (!hasProfileEmail && !hasEmailField) {
      return [
        ...fields,
        {
          displayName: 'Email',
          type: 'email',
          isRequired: true,
          id: injectedEmailFieldId,
          metaData: null,
        },
      ];
    }
    return fields;
  }, [enrollmentFields, hasProfileEmail]);
  // Initial form state
  const initialFormState = {
    values: effectiveEnrollmentFields.reduce((acc, field) => {
      acc[field.id] = '';
      return acc;
    }, {}),
    errors: {},
  };

  const [state, dispatch] = useReducer(formReducer, initialFormState);
  const [addressSuggestions, setAddressSuggestions] = useState({});
  const [showSuggestions, setShowSuggestions] = useState({});
  const [isLoading, setIsLoading] = useState({});
  // Add loading state for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const addressRefs = useRef({});

  useEffect(() => {
    if (!userDetails?._id) return;

    const existingEnrollmentFields =
      userLoyaltyDetails?.enrollmentFields &&
      typeof userLoyaltyDetails.enrollmentFields === 'object' &&
      !Array.isArray(userLoyaltyDetails.enrollmentFields)
        ? userLoyaltyDetails.enrollmentFields
        : {};

    effectiveEnrollmentFields.forEach((field) => {
      const label = field.displayName;
      const existingValue =
        existingEnrollmentFields[String(field.id)] ??
        existingEnrollmentFields[field.id];
      let prefillValue =
        existingValue !== undefined && existingValue !== null
          ? existingValue
          : '';

      if (
        (prefillValue === '' || prefillValue === null) &&
        label.includes('Name') &&
        userDetails.firstName
      ) {
        const fullName = `${userDetails.firstName} ${
          userDetails.lastName || ''
        }`.trim();
        prefillValue = fullName;
      } else if (
        (prefillValue === '' || prefillValue === null) &&
        field.type === 'email' &&
        userDetails.email
      ) {
        prefillValue = userDetails.email;
      } else if (
        (prefillValue === '' || prefillValue === null) &&
        field.type === 'phone_number' &&
        (userDetails.mobileNumber || userDetails.intlPhoneNumber)
      ) {
        prefillValue = userDetails.mobileNumber || userDetails.intlPhoneNumber;
      }

      if (prefillValue !== '' && prefillValue !== null) {
        dispatch({ type: 'UPDATE_FIELD', field: field.id, value: prefillValue });
      }
    });
  }, [userDetails, userLoyaltyDetails, effectiveEnrollmentFields]);

  // Add click outside handler
  useEffect(() => {
    function handleClickOutside(event) {
      // For each field that has suggestions showing
      Object.keys(showSuggestions).forEach((fieldId) => {
        if (
          showSuggestions[fieldId] &&
          addressRefs.current[fieldId] &&
          !addressRefs.current[fieldId].contains(event.target)
        ) {
          setShowSuggestions((prev) => ({ ...prev, [fieldId]: false }));
        }
      });
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions]);

  const handleChange = (id, value) => {
    dispatch({ type: 'UPDATE_FIELD', field: id, value });
  };

  const validatePhoneNumber = (number, countryCode) => {
    const parsed = parsePhoneNumberFromString(number, countryCode);
    console.log(parsed);
    return parsed && parsed.isValid();
  };

  const validateForm = () => {
    const countryCode = storeConfig?.countryIdentifier || 'AU';
    const newErrors = {};
    effectiveEnrollmentFields.forEach((field) => {
      const value = state.values[field.id];
      if (field.isRequired && !value) {
        newErrors[field.id] = `${field.displayName} is required.`;
      } else if (field.type === 'number' && value) {
        if (isNaN(value)) {
          newErrors[field.id] = `${field.displayName} must be a number.`;
        }
      } else if (field.type === 'phone_number' && value) {
        if (!validatePhoneNumber(value, countryCode)) {
          newErrors[
            field.id
          ] = `Please enter a valid phone number for ${countryCode}.`;
        }
      } else if (field.type === 'post_code' && value) {
        if (!validatePostCode(value, countryCode)) {
          newErrors[
            field.id
          ] = `Please enter a valid postcode for ${countryCode}.`;
        }
      } else if (field.type === 'email' && value) {
        if (!validateEmail(value)) {
          newErrors[field.id] = 'Please enter a valid email address';
        }
      }
    });
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      dispatch({ type: 'SET_ERRORS', errors });
      return;
    }

    // Set loading state
    setIsSubmitting(true);

    if (userDetails && storeLoyaltySettings) {
      try {
        const { intlPhoneNumber, firstName, lastName, email, _id } =
          userDetails;
        
        let userEmail = email || null;
        
        // Only check enrollment fields if user doesn't have email in profile
        if (!userEmail) {
          const emailField = effectiveEnrollmentFields.find(
            (field) => field.type === 'email'
          );
          userEmail = emailField ? state.values[emailField.id] : null;
        }
        const originalFieldIds = new Set(
          (Array.isArray(enrollmentFields) ? enrollmentFields : []).map(
            (field) => String(field.id)
          )
        );
        const filteredEnrollmentFieldValues = Object.entries(
          state.values || {}
        ).reduce((acc, [key, value]) => {
          if (originalFieldIds.has(String(key))) {
            acc[key] = value;
          }
          return acc;
        }, {});
        if (userLoyaltyDetails === null) {
          const loyaltySignupPayload = {
            mobileNumber: intlPhoneNumber,
            firstName,
            lastName,
            email: userEmail,
            userId: _id,
            storeId,
            restaurantId,
            enrollmentFields: filteredEnrollmentFieldValues,
            settingId: storeLoyaltySettings.id,
            level: storeLoyaltySettings?.level || LOYALTY_DEFAULTS.LEVEL,
            enrollmentType: storeLoyaltySettings.enrollmentType,
          };

          if (referralId && referralId.trim() !== '') {
            loyaltySignupPayload.referralId = referralId;
          }

          const loyaltySignupResponse = await reduxDispatch(
            createLoyaltyUserSignup(loyaltySignupPayload)
          );

          if (isLoyalty && orderId && loyaltySignupResponse?.userId) {
            await reduxDispatch(
              assignPointsToUserScan({
                orderId,
                mobileNumber: intlPhoneNumber,
                restaurantId,
                userId: loyaltySignupResponse.userId,
                storeId,
              })
            );
            sessionStorage.setItem(`loyaltyScan_${orderId}`, '1');
          }
        } else {
          const mergedEnrollmentFields = {
            ...(userLoyaltyDetails?.enrollmentFields || {}),
            ...filteredEnrollmentFieldValues,
          };
          if (userEmail && !mergedEnrollmentFields['1']) {
            mergedEnrollmentFields['1'] = userEmail;
          }

          const updatedLoyaltyAccount = await updateLoyaltyAccount({
            userId: _id,
            settingId: storeLoyaltySettings.id,
            updateData: {
              firstName: firstName || userLoyaltyDetails.firstName || '',
              lastName: lastName || userLoyaltyDetails.lastName || '',
              email: userEmail || userLoyaltyDetails.email || '',
              mobileNumber:
                intlPhoneNumber ||
                userDetails.mobileNumber ||
                userLoyaltyDetails.mobileNumber ||
                '',
              enrollmentFields: mergedEnrollmentFields,
            },
          });

          if (updatedLoyaltyAccount) {
            reduxDispatch(fetchLoyaltyDetailsSuccess(updatedLoyaltyAccount));
            if (pendingRedemptionIntent?.redemptionId) {
              await handleRedemption({
                redemptionId: pendingRedemptionIntent.redemptionId,
                userId: pendingRedemptionIntent.userId || _id,
                storeId: pendingRedemptionIntent.storeId || storeId,
                restaurantId:
                  pendingRedemptionIntent.restaurantId || restaurantId,
                level: pendingRedemptionIntent.level || 0,
                dispatch: reduxDispatch,
              });
              reduxDispatch(clearPendingRedemptionIntent());
            }
            reduxDispatch(showEnrollmentForm(false));
          }
        }
      } catch (error) {
        console.error(
          'Error during loyalty signup or points assignment:',
          error
        );
      } finally {
        // Always clear loading state
        setIsSubmitting(false);
      }
    } else {
      // Clear loading state if user is already enrolled or no user details
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !isSubmitting) {
      handleSubmit(e);
    }
  };

  const baseClasses =
    'bg-white border border-gray-300 text-gray-900 text-base rounded-xl focus:outline-none focus:ring-0 focus:border-blue-500 block w-full px-4 py-3 shadow-sm placeholder-gray-400';

  // Function to search addresses
  const searchAddressesHandler = useCallback(
    async (fieldId, input) => {
      if (!input || input.length < 3) {
        setAddressSuggestions((prev) => ({ ...prev, [fieldId]: [] }));
        return;
      }

      setIsLoading((prev) => ({ ...prev, [fieldId]: true }));
      try {
        const data = await searchAddresses({
          input,
          types: 'address',
          country: storeConfig?.countryIdentifier?.toLowerCase() || 'au',
          requestFrom: 'website',
          restaurantId,
          storeId,
          cloudFunctionsUrl: storeConfig.cloudFunctionsUrl,
        });

        console.log(`zzz data`, data, storeConfig);
        if (data && Array.isArray(data.results?.predictions)) {
          setAddressSuggestions((prev) => ({
            ...prev,
            [fieldId]: data.results?.predictions,
          }));
          setShowSuggestions((prev) => ({ ...prev, [fieldId]: true }));
        }
      } catch (error) {
        console.error('Error fetching address suggestions:', error);
        setAddressSuggestions((prev) => ({ ...prev, [fieldId]: [] }));
      } finally {
        setIsLoading((prev) => ({ ...prev, [fieldId]: false }));
      }
    },
    [storeConfig, restaurantId, storeId]
  );

  // Debounced address search
  const debouncedSearchAddresses = useCallback(
    debounce((fieldId, input) => searchAddressesHandler(fieldId, input), 300),
    [searchAddressesHandler]
  );

  // Function to get address details by placeId
  const getAddressDetailsHandler = async (fieldId, placeId) => {
    try {
      const data = await getAddressByPlaceId({
        placeId,
        requestFrom: 'website',
        restaurantId,
        storeId,
        cloudFunctionsUrl: storeConfig.cloudFunctionsUrl,
      });

      console.log(`zzz data`, data);
      if (data) {
        // Update the field with formatted address
        const formattedAddress = data.results?.result?.formatted_address;
        const location = data.results?.result?.geometry?.location;
        if (formattedAddress && location) {
          dispatch({
            type: 'UPDATE_FIELD',
            field: fieldId,
            value: { formattedAddress, location },
          });
        }
      }
    } catch (error) {
      console.error('Error fetching address details:', error);
    } finally {
      setShowSuggestions((prev) => ({ ...prev, [fieldId]: false }));
    }
  };

  const handleAddressChange = (fieldId, value) => {
    dispatch({ type: 'UPDATE_FIELD', field: fieldId, value });
    debouncedSearchAddresses(fieldId, value);
  };

  const handleAddressSelect = (fieldId, suggestion) => {
    dispatch({
      type: 'UPDATE_FIELD',
      field: fieldId,
      value: suggestion.description,
    });
    getAddressDetailsHandler(fieldId, suggestion.place_id);
    setShowSuggestions((prev) => ({ ...prev, [fieldId]: false }));
  };

  const clearAddressField = (fieldId) => {
    dispatch({
      type: 'UPDATE_FIELD',
      field: fieldId,
      value: { formattedAddress: '', location: {} },
    });
    setAddressSuggestions((prev) => ({ ...prev, [fieldId]: [] }));
    setShowSuggestions((prev) => ({ ...prev, [fieldId]: false }));
  };

  const renderInput = (field) => {
    const error = state.errors[field.id];
    const errorClass = error
      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
      : '';
    const inputClasses = `${baseClasses} ${errorClass}`;

    const commonProps = {
      required: field.isRequired,
      className: inputClasses,
      value: state.values[field.id],
      onKeyDown: handleKeyDown,
    };

    switch (field.type) {
      case 'string':
        return (
          <>
            <input
              type='text'
              {...commonProps}
              onChange={(e) => handleChange(field.id, e.target.value)}
            />
            {error && <p className='text-red-600 text-sm mt-1'>{error}</p>}
          </>
        );
      case 'address':
        return (
          <>
            <div
              className='relative'
              ref={(el) => {
                addressRefs.current[field.id] = el;
              }}
            >
              <input
                type='text'
                placeholder='Enter address'
                {...commonProps}
                onChange={(e) => handleAddressChange(field.id, e.target.value)}
                onFocus={() =>
                  state.values[field.id] &&
                  addressSuggestions[field.id]?.length > 0 &&
                  setShowSuggestions((prev) => ({ ...prev, [field.id]: true }))
                }
                value={state.values[field.id]?.formattedAddress}
                style={{ width: 'calc(100% - 40px)' }}
              />
              {isLoading[field.id] && (
                <div className='absolute right-3 top-3'>
                  <div className='animate-spin h-5 w-5 border-2 border-gray-500 rounded-full border-t-transparent'></div>
                </div>
              )}
              {!isLoading[field.id] && state.values[field.id] && (
                <button
                  type='button'
                  className='absolute right-3 top-3 text-gray-400 hover:text-gray-600'
                  onClick={() => clearAddressField(field.id)}
                >
                  <Close className='h-5 w-5' />
                </button>
              )}
              {showSuggestions[field.id] &&
                addressSuggestions[field.id]?.length > 0 && (
                  <div className='absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm'>
                    {addressSuggestions[field.id].map((suggestion, index) => (
                      <div
                        key={index}
                        className='cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100'
                        onClick={() =>
                          handleAddressSelect(field.id, suggestion)
                        }
                      >
                        <span className='block truncate'>
                          {suggestion.description}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
            </div>
            {error && <p className='text-red-600 text-sm mt-1'>{error}</p>}
          </>
        );
      case 'number':
        return (
          <>
            <input
              type='number'
              {...commonProps}
              onChange={(e) => handleChange(field.id, e.target.value)}
            />
            {error && <p className='text-red-600 text-sm mt-1'>{error}</p>}
          </>
        );
      case 'date':
        return (
          <>
            <input
              type='date'
              {...commonProps}
              className={`${commonProps.className} text-left enrollmentDateInput`}
              onChange={(e) => handleChange(field.id, e.target.value)}
            />
            {error && <p className='text-red-600 text-sm mt-1'>{error}</p>}
          </>
        );
      case 'boolean':
        return (
          <>
            <select
              {...commonProps}
              onChange={(e) => handleChange(field.id, e.target.value)}
            >
              <option value=''>Select</option>
              <option value='yes'>Yes</option>
              <option value='no'>No</option>
            </select>
            {error && <p className='text-red-600 text-sm mt-1'>{error}</p>}
          </>
        );
      case 'dropdown':
        return (
          <>
            <select
              {...commonProps}
              onChange={(e) => handleChange(field.id, e.target.value)}
            >
              <option value=''>Select</option>
              {field.metaData?.options?.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.value}
                </option>
              ))}
            </select>
            {error && <p className='text-red-600 text-sm mt-1'>{error}</p>}
          </>
        );
      case 'phone_number':
        return (
          <>
            <input
              type='tel'
              {...commonProps}
              placeholder='Enter phone number'
              onChange={(e) => {
                const formatter = new AsYouType(
                  storeConfig?.countryIdentifier || 'AU'
                );
                const formatted = formatter.input(e.target.value);
                handleChange(field.id, formatted);
              }}
              style={{ borderRadius: '12px' }}
            />
            {error && <p className='text-red-600 text-sm mt-1'>{error}</p>}
          </>
        );
      case 'post_code':
        return (
          <>
            <input
              type='text'
              {...commonProps}
              placeholder='Enter postcode'
              onChange={(e) => handleChange(field.id, e.target.value)}
            />
            {error && <p className='text-red-600 text-sm mt-1'>{error}</p>}
          </>
        );
      case 'email':
        return (
          <>
            <input
              type='email'
              {...commonProps}
              placeholder='Enter email address'
              onChange={(e) => handleChange(field.id, e.target.value)}
            />
            {error && <p className='text-red-600 text-sm mt-1'>{error}</p>}
          </>
        );
      default:
        return null;
    }
  };

  const onCloseEnrollment = () => {
    reduxDispatch(clearPendingRedemptionIntent());
    reduxDispatch(showEnrollmentForm(false));
  };

  return (
    <div className='relative h-full min-h-0 pt-4 px-5 flex flex-col'>
      {/* Loading Overlay */}
      {isSubmitting && (
        <div className='absolute inset-0 bg-white bg-opacity-95 z-50 flex items-center justify-center'>
          <style>{`
            .status-text {
              font-size: 1.5em !important;
            }
          `}</style>
          <AnimationWrapper animation='fade-in'>
            <LottieAnimation
              statusText={loadingStatusText}
              size='normal'
              backgroundColor={themeColors?.backgroundColor}
            />
          </AnimationWrapper>
        </div>
      )}

      <CloseButtonCWUI onClick={onCloseEnrollment} />
      <h2 className='detailsTitle' style={{ marginTop: '0px' }}>
        {userLoyaltyDetails
          ? 'Complete your signup form to continue redeeming rewards'
          : 'Please fill the enrollment fields to continue'}
      </h2>

      {/* Scrollable Form Container */}
      <div className='flex-1 min-h-0 overflow-y-auto overscroll-contain pb-4'>
        <form onSubmit={handleSubmit} className='space-y-6'>
          {effectiveEnrollmentFields.map((field) => (
            <div key={field.id}>
              <label className='block font-normal mb-2 text-[17px] text-gray-900'>
                {field.displayName}
                {field.isRequired ? (
                  <span className='text-red-500 ml-1'>*</span>
                ) : (
                  <span className='text-gray-400 ml-1'>(Optional)</span>
                )}
              </label>
              {renderInput(field)}
            </div>
          ))}
        </form>
      </div>

      {/* Sticky Save Button */}
      <div className='sticky bottom-0 z-10 shrink-0 w-full p-4 pt-2 pb-[max(1rem,env(safe-area-inset-bottom))] bg-white border-t border-gray-100 max-lg:mb-[63px]'>
        <ButtonUI
          fullWidth
          size='xl'
          rounded='xl'
          variant='text'
          color='accent'
          fontSize='lg'
          extendedClassName='shadow-cw'
          style={signupButtonStyle}
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {userLoyaltyDetails ? 'Complete profile' : 'Sign up to loyalty'}
        </ButtonUI>
      </div>
    </div>
  );
}
